/**
 * ExportButton Component - Clean Architecture Export UI Component
 *
 * A reusable export button component that follows clean architecture principles.
 * It provides a clean interface for table export functionality.
 */

import React, {useState} from 'react';
import {Download, FileText, Table} from 'lucide-react';
import Button from './Button';
import {ExportFormat} from '../../../types/export.types';

interface ExportButtonProps {
  onExport: (format: ExportFormat) => void;
  isExporting?: boolean;
  canExport?: boolean;
  recordCount?: number;
  className?: string;
  showDropdown?: boolean;
  disabled?: boolean;
}

const ExportButton: React.FC<ExportButtonProps> = ({
  onExport,
  isExporting = false,
  canExport = true,
  recordCount = 0,
  className = '',
  showDropdown = true,
  disabled = false
}) => {
  const [showMenu, setShowMenu] = useState(false);

  const handleExport = (format: ExportFormat) => {
    onExport(format);
    setShowMenu(false);
  };

  const exportOptions = [
    {
      format: 'csv' as const,
      label: 'تصدير CSV',
      icon: <FileText className="w-4 h-4" />,
      description: 'ملف CSV قابل للتحرير'
    },
    {
      format: 'excel' as const,
      label: 'تصدير Excel',
      icon: <Table className="w-4 h-4" />,
      description: 'ملف Excel متقدم'
    }
  ];

  if (!showDropdown) {
    return (
      <Button
        variant="outline"
        onClick={() => handleExport('csv')}
        disabled={disabled || !canExport || isExporting}
        isLoading={isExporting}
        className={`flex items-center gap-2 ${className}`}
        icon={<Download className="w-4 h-4" />}
      >
        {isExporting ? 'جاري التصدير...' : 'تصدير CSV'}
        {recordCount > 0 && !isExporting && (
          <span className="bg-brand-100 text-brand-700 text-xs px-2 py-1 rounded-full">
            {recordCount}
          </span>
        )}
      </Button>
    );
  }

  return (
    <div className={`relative group ${className}`}>
      <Button
        variant="outline"
        onClick={() => setShowMenu(!showMenu)}
        disabled={disabled || !canExport || isExporting}
        isLoading={isExporting}
        className="flex items-center gap-2"
        icon={<Download className="w-4 h-4" />}
      >
        {isExporting ? 'جاري التصدير...' : 'تصدير'}
        {recordCount > 0 && !isExporting && (
          <span className="bg-brand-100 text-brand-700 text-xs px-2 py-1 rounded-full">
            {recordCount}
          </span>
        )}
      </Button>

      {showMenu && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-48">
          <div className="py-1">
            {exportOptions.map((option) => (
              <button
                key={option.format}
                onClick={() => handleExport(option.format)}
                disabled={disabled || !canExport || isExporting}
                className="flex items-center gap-3 w-full text-right px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.icon}
                <div className="flex-1">
                  <div className="font-medium">{option.label}</div>
                  <div className="text-xs text-gray-500">{option.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Backdrop to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
};

export default ExportButton;
